/**
 * Cell Allocation — data access for a cycle's bands and their consumption.
 *
 * Reads: the bands table (RLS: workspace members) and the read model
 * get_cell_allocation_consumption (migrations 816, 972).
 * Writes: set_cell_allocation and record_cell_allocation_opened only — neither
 * table has a client write policy (build contract 2.3c, one write path per act).
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { CellAllocationBandDraft, CellRef, ShareVerdict } from './cellAllocation';

export interface CellAllocationBand extends CellAllocationBandDraft {
  id: string;
  position: number;
}

export interface CellConsumption extends CellRef {
  bandId: string | null;
  committedUnits: number;
  committedItems: number;
  proposedUnits: number;
  proposedItems: number;
}

export interface BandConsumption extends CellAllocationBand {
  committedUnits: number;
  committedItems: number;
  committedShare: number | null;
  proposedUnits: number;
  proposedItems: number;
  projectedShare: number | null;
  /** Above-the-line share minus planned share, in points (migration 861). */
  gapPoints: number | null;
  verdict: ShareVerdict;
}

export interface CellAllocationConsumption {
  cycleId: string;
  basis: string;
  tolerancePoints: number;
  /** The bands' shares added up. May be under or over 100 (owner ruling R-1, migration 972). */
  shareTotal: number;
  /** When the cycle owner (or an admin) first opened the Allocation tab; null until then (972). */
  firstOpenedAt: string | null;
  /** Present when the read was made "as if" a proposal were accepted (972). */
  ifAccepted: { proposalId: string; itemsCommittedByAccepting: number } | null;
  totals: { committedUnits: number; committedItems: number; proposedUnits: number; proposedItems: number };
  bands: BandConsumption[];
  unallocated: {
    sharePercent: number;
    committedUnits: number;
    committedItems: number;
    committedShare: number | null;
    proposedUnits: number;
    proposedItems: number;
    cells: Omit<CellConsumption, 'bandId'>[];
  };
  cells: CellConsumption[];
  unplaced: { committedUnits: number; committedItems: number; proposedUnits: number; proposedItems: number };
}

type RawCell = { item_category: string; altitude_level: number };

function num(v: unknown): number {
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : 0;
}
function numOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : null;
}
function toCells(raw: unknown): CellRef[] {
  if (!Array.isArray(raw)) return [];
  return (raw as RawCell[])
    .filter((c) => c && typeof c.item_category === 'string' && Number.isFinite(Number(c.altitude_level)))
    .map((c) => ({ itemCategory: c.item_category, altitudeLevel: Number(c.altitude_level) }));
}

export async function fetchCellAllocationBands(cycleId: string): Promise<CellAllocationBand[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('cell_allocation_bands')
    .select('id, name, share_percent, position, cells')
    .eq('cycle_id', cycleId)
    .order('position', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    sharePercent: num(row.share_percent),
    position: row.position,
    cells: toCells(row.cells),
  }));
}

export interface SaveCellAllocationResult {
  success: boolean;
  code?: string;
  error?: string;
  bandCount?: number;
  shareTotal?: number;
  /** The saved shares add up to more than 100 (saved anyway; the screen warns). */
  over100?: boolean;
}

/** Replace the cycle's bands. The server re-checks every rule; a refusal comes back as { success: false, code, error }. */
export async function saveCellAllocation(
  cycleId: string,
  bands: readonly CellAllocationBandDraft[],
): Promise<SaveCellAllocationResult> {
  const payload = bands.map((b) => ({
    name: b.name.trim(),
    share_percent: b.sharePercent,
    cells: b.cells.map((c) => ({ item_category: c.itemCategory.toLowerCase(), altitude_level: c.altitudeLevel })),
  }));
  const { data, error } = await getTypedSupabaseClient().rpc('set_cell_allocation', {
    p_cycle_id: cycleId,
    p_bands: payload,
  });
  if (error) throw new Error(error.message);
  const r = (data ?? {}) as Record<string, unknown>;
  return {
    success: r.success === true,
    code: typeof r.code === 'string' ? r.code : undefined,
    error: typeof r.error === 'string' ? r.error : undefined,
    bandCount: numOrNull(r.band_count) ?? undefined,
    shareTotal: numOrNull(r.share_total) ?? undefined,
    over100: r.over_100 === true,
  };
}

/**
 * Record that the cycle owner opened the Allocation tab. The accept-time
 * warning waits for the first time (owner ruling, Oct 14). Idempotent; for a
 * person who does not set the shares the server records nothing.
 */
export async function recordCellAllocationOpened(cycleId: string): Promise<void> {
  const { error } = await getTypedSupabaseClient().rpc('record_cell_allocation_opened', { p_cycle_id: cycleId });
  if (error) throw new Error(error.message);
}

/**
 * The read model. With `ifAcceptedProposalId`, the items that proposal would
 * ratify count as committed: the bands as they would stand if it were accepted.
 */
export async function fetchCellAllocationConsumption(
  cycleId: string,
  opts: { ifAcceptedProposalId?: string } = {},
): Promise<CellAllocationConsumption> {
  const { data, error } = await getTypedSupabaseClient().rpc('get_cell_allocation_consumption', {
    p_cycle_id: cycleId,
    ...(opts.ifAcceptedProposalId ? { p_if_accepted_proposal_id: opts.ifAcceptedProposalId } : {}),
  });
  if (error) throw new Error(error.message);
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.success !== true) throw new Error(typeof r.error === 'string' ? r.error : 'Could not read the allocation.');
  const totals = (r.totals ?? {}) as Record<string, unknown>;
  const un = (r.unallocated ?? {}) as Record<string, unknown>;
  const unplaced = (r.unplaced ?? {}) as Record<string, unknown>;
  const ifAccepted = r.if_accepted as Record<string, unknown> | null | undefined;
  const cellOf = (c: Record<string, unknown>) => ({
    itemCategory: String(c.item_category ?? ''),
    altitudeLevel: num(c.altitude_level),
    committedUnits: num(c.committed_units),
    committedItems: num(c.committed_items),
    proposedUnits: num(c.proposed_units),
    proposedItems: num(c.proposed_items),
  });
  return {
    cycleId: String(r.cycle_id ?? cycleId),
    basis: String(r.basis ?? ''),
    tolerancePoints: num(r.tolerance_points),
    shareTotal: num(r.share_total),
    firstOpenedAt: typeof r.first_opened_at === 'string' ? r.first_opened_at : null,
    ifAccepted: ifAccepted && typeof ifAccepted === 'object'
      ? { proposalId: String(ifAccepted.proposal_id ?? ''), itemsCommittedByAccepting: num(ifAccepted.items_committed_by_accepting) }
      : null,
    totals: {
      committedUnits: num(totals.committed_units),
      committedItems: num(totals.committed_items),
      proposedUnits: num(totals.proposed_units),
      proposedItems: num(totals.proposed_items),
    },
    bands: ((r.bands as Record<string, unknown>[]) ?? []).map((b) => ({
      id: String(b.id),
      name: String(b.name ?? ''),
      position: num(b.position),
      sharePercent: num(b.share_percent),
      cells: toCells(b.cells),
      committedUnits: num(b.committed_units),
      committedItems: num(b.committed_items),
      committedShare: numOrNull(b.committed_share),
      proposedUnits: num(b.proposed_units),
      proposedItems: num(b.proposed_items),
      projectedShare: numOrNull(b.projected_share),
      gapPoints: numOrNull(b.gap_points),
      verdict: (['over', 'under', 'on', 'empty'].includes(String(b.verdict)) ? String(b.verdict) : 'empty') as ShareVerdict,
    })),
    unallocated: {
      sharePercent: num(un.share_percent),
      committedUnits: num(un.committed_units),
      committedItems: num(un.committed_items),
      committedShare: numOrNull(un.committed_share),
      proposedUnits: num(un.proposed_units),
      proposedItems: num(un.proposed_items),
      cells: ((un.cells as Record<string, unknown>[]) ?? []).map(cellOf),
    },
    cells: ((r.cells as Record<string, unknown>[]) ?? []).map((c) => ({
      ...cellOf(c),
      bandId: c.band_id == null ? null : String(c.band_id),
    })),
    unplaced: {
      committedUnits: num(unplaced.committed_units),
      committedItems: num(unplaced.committed_items),
      proposedUnits: num(unplaced.proposed_units),
      proposedItems: num(unplaced.proposed_items),
    },
  };
}
